/**
 * Safely highlights search terms in HTML content without breaking HTML tags
 * Only highlights text nodes, preserving HTML structure and attributes
 */

interface HighlightOptions {
  searchTerm: string
  content: string
  highlightStyle?: string
  skipTags?: string[] // Tags to skip highlighting within (e.g., ['CODE', 'MARK', 'SCRIPT'])
  caseSensitive?: boolean
}

/**
 * Escapes special regex characters to prevent regex injection
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Highlights search terms in HTML content while preserving HTML structure
 *
 * @param options - Configuration for highlighting
 * @returns HTML string with highlighted search terms wrapped in <mark> tags
 */
export const highlightHtmlContent = ({
  searchTerm,
  content,
  highlightStyle = 'background-color: #ffeb3b; padding: 0 2px;',
  skipTags = ['CODE', 'MARK', 'SCRIPT', 'STYLE', 'SVG'],
  caseSensitive = false,
}: HighlightOptions): string => {
  // Return original content if no search term or content
  if (!searchTerm || !content) return content

  // Parse HTML string into DOM
  const parser = new DOMParser()
  const doc = parser.parseFromString(content, 'text/html')

  // Create regex pattern with escaped search term
  const flags = caseSensitive ? 'g' : 'gi'
  const pattern = new RegExp(`(${escapeRegex(searchTerm)})`, flags)

  /**
   * Recursively walks through DOM nodes and highlights text nodes only
   */
  const highlightTextNodes = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || ''

      // Check if text contains the search term
      if (pattern.test(text)) {
        // Reset regex lastIndex for proper replacement
        pattern.lastIndex = 0

        const highlightedText = text.replace(
          pattern,
          match => `<mark style="${highlightStyle}">${match}</mark>`,
        )

        // Create a document fragment from the highlighted text
        const range = doc.createRange()
        const fragment = range.createContextualFragment(highlightedText)

        // Replace the text node with the highlighted fragment
        if (node.parentNode) {
          node.parentNode.replaceChild(fragment, node)
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element

      // Skip certain tags to avoid breaking functionality
      if (skipTags.includes(element.nodeName.toUpperCase())) {
        return
      }

      // Process child nodes (convert NodeList to array to avoid live collection issues)
      const childNodes = Array.from(node.childNodes)
      childNodes.forEach(child => {
        highlightTextNodes(child)
      })
    }
  }

  // Start highlighting from the body element
  if (doc.body) {
    highlightTextNodes(doc.body)
    return doc.body.innerHTML
  }

  // Fallback to original content if parsing failed
  return content
}

/**
 * Hook for components that need to highlight HTML content
 * Returns the highlighted HTML string ready for dangerouslySetInnerHTML
 */
export const useHighlightedHtml = (
  content: string,
  searchTerm: string | null,
  options?: Partial<HighlightOptions>,
): string => {
  if (!searchTerm) return content

  return highlightHtmlContent({
    searchTerm,
    content,
    ...options,
  })
}
